import React, { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ASTUCES, ASTUCE_CATS, ASTUCE_LINKS } from '../data/astuces';
import './Astuces.css';

/**
 * 📚 Le Guide Appee — toutes les astuces de l'app.
 * Grille par catégorie + popup détaillée (objectif / comment / pourquoi / bénéfice).
 * Bouton « astuce au hasard » qui tourne et ne redonne jamais la même de suite.
 */
export default function Astuces() {
  const [cat, setCat] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState('');

  // Filtre = catégorie ET recherche live (tous les mots doivent matcher), sur
  // titre + objectif + comment + pourquoi + bénéfice + libellé de catégorie.
  const list = useMemo(() => {
    let l = cat === 'all' ? ASTUCES : ASTUCES.filter(a => a.cat === cat);
    const query = q.trim().toLowerCase();
    if (query) {
      const words = query.split(/\s+/);
      l = l.filter(a => {
        const hay = `${a.title} ${a.objectif} ${a.comment} ${a.pourquoi} ${a.benefice} ${ASTUCE_CATS[a.cat]?.label || ''}`.toLowerCase();
        return words.every(w => hay.includes(w));
      });
    }
    return l;
  }, [cat, q]);

  const current = openId ? ASTUCES.find(a => a.id === openId) : null;

  // Astuce au hasard, jamais la même que celle affichée
  const randomAstuce = useCallback((excludeId) => {
    const pool = ASTUCES.filter(a => a.id !== excludeId);
    return pool[Math.floor(Math.random() * pool.length)];
  }, []);

  const openRandom = () => setOpenId(randomAstuce(openId).id);
  const nextAstuce = () => setOpenId(randomAstuce(openId).id);

  return (
    <div className="astuces">
      {/* Hero */}
      <div className="ast-hero">
        <div className="ast-hero-nebula" />
        <div className="ast-hero-inner">
          <div className="ast-hero-left">
            <div className="ast-hero-icon">📚</div>
            <div>
              <h1 className="ast-hero-title">Le Guide Appee</h1>
              <p className="ast-hero-sub">
                {ASTUCES.length} astuces pour faire décoller ton commerce — l'objectif, le
                « comment », et pourquoi c'est indispensable.
              </p>
            </div>
          </div>
          <button className="ast-hero-cta" onClick={openRandom}>
            🎲 Une astuce au hasard
          </button>
        </div>
      </div>

      {/* Recherche live — « tape ta demande » */}
      <div className="ast-search-wrap">
        <svg className="ast-search-ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        <input
          className="ast-search"
          type="text"
          placeholder="Tape ta demande ou un mot-clé… (ex : anniversaire, avis Google, SMS)"
          value={q}
          onChange={e => setQ(e.target.value)}
          aria-label="Rechercher une astuce"
        />
        {q && (
          <button className="ast-search-clear" onClick={() => setQ('')} aria-label="Effacer">✕</button>
        )}
        <span className="ast-search-count">{list.length} résultat{list.length > 1 ? 's' : ''}</span>
      </div>

      {/* Filtres catégories */}
      <div className="ast-filters">
        <button
          className={`ast-chip ${cat === 'all' ? 'on' : ''}`}
          onClick={() => setCat('all')}
        >
          ✨ Tout <span className="ast-chip-n">{ASTUCES.length}</span>
        </button>
        {Object.entries(ASTUCE_CATS).map(([key, c]) => {
          const n = ASTUCES.filter(a => a.cat === key).length;
          if (!n) return null;
          return (
            <button
              key={key}
              className={`ast-chip ${cat === key ? 'on' : ''}`}
              style={{ '--cat': c.color }}
              onClick={() => setCat(key)}
            >
              {c.icon} {c.label} <span className="ast-chip-n">{n}</span>
            </button>
          );
        })}
      </div>

      {/* Grille */}
      <div className="ast-grid">
        {list.map(a => {
          const c = ASTUCE_CATS[a.cat];
          return (
            <button
              key={a.id}
              className="ast-card"
              style={{ '--cat': c.color }}
              onClick={() => setOpenId(a.id)}
            >
              <span className="ast-card-ic">{a.icon}</span>
              <span className="ast-card-title">{a.title}</span>
              <span className="ast-card-cat">{c.icon} {c.label}</span>
              <span className="ast-card-obj">{a.objectif}</span>
              <span className="ast-card-more">Lire l'astuce →</span>
            </button>
          );
        })}
      </div>

      {list.length === 0 && (
        <div className="ast-empty">
          <div className="ast-empty-ic">🔍</div>
          <div className="ast-empty-title">Aucune astuce pour « {q} »</div>
          <div className="ast-empty-sub">Essaie un autre mot (ex : « anniversaire », « avis », « SMS »)…</div>
          <button className="ast-empty-reset" onClick={() => { setQ(''); setCat('all'); }}>Tout afficher</button>
        </div>
      )}

      {/* Popup détaillée */}
      {current && (() => {
        const c = ASTUCE_CATS[current.cat];
        return (
          <div className="ast-modal-backdrop" onClick={() => setOpenId(null)}>
            <div className="ast-modal" style={{ '--cat': c.color }} onClick={e => e.stopPropagation()}>
              <button className="ast-modal-close" onClick={() => setOpenId(null)} aria-label="Fermer">✕</button>
              <div className="ast-modal-head">
                <span className="ast-modal-ic">{current.icon}</span>
                <div>
                  <span className="ast-modal-cat">{c.icon} {c.label}</span>
                  <h2 className="ast-modal-title">{current.title}</h2>
                </div>
              </div>

              <div className="ast-block">
                <span className="ast-block-lbl">🎯 L'objectif</span>
                <p>{current.objectif}</p>
              </div>
              <div className="ast-block">
                <span className="ast-block-lbl">⚙️ Comment ça marche</span>
                <p>{current.comment}</p>
              </div>
              <div className="ast-block">
                <span className="ast-block-lbl">💡 Pourquoi c'est essentiel</span>
                <p>{current.pourquoi}</p>
              </div>
              <div className="ast-block ast-block-benef">
                <span className="ast-block-lbl">✨ Le bénéfice pour toi</span>
                <p>{current.benefice}</p>
              </div>

              {ASTUCE_LINKS[current.id] && (
                <Link to={ASTUCE_LINKS[current.id].to} state={ASTUCE_LINKS[current.id].state} className="ast-launch" onClick={() => setOpenId(null)}>
                  🚀 {ASTUCE_LINKS[current.id].label} →
                </Link>
              )}

              <div className="ast-modal-foot">
                <button className="ast-next" onClick={nextAstuce}>🎲 Astuce suivante</button>
                <button className="ast-done" onClick={() => setOpenId(null)}>J'ai compris</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
